import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { formatUsd } from './TenantDetail';

interface OverviewRow {
  tenant_id: string;
  name: string;
  slug: string;
  plan: string;
  environment: string;
  message_count: number;
  cost_usd: number;
}

interface Overview {
  period: string;
  model: string;
  total_cost_usd: number;
  tenants: OverviewRow[];
}

export default function AiUsage() {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  function load() {
    setLoading(true);
    setError('');
    api.get<Overview>('/ai-usage/overview')
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-brand-600" /> Claude API-kosten
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Verbruik van de ontwerp-assistent per tenant{data ? `, deze maand (${data.period})` : ''}.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="btn-secondary text-sm">
          {loading ? 'Laden...' : 'Vernieuwen'}
        </button>
      </div>

      {error && (
        <div className="card p-4 flex items-center gap-2 text-red-600 text-sm">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      )}

      {data && (
        <>
          <div className="card p-6">
            <p className="text-sm text-gray-500">Totaal deze maand (alle tenants)</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{formatUsd(data.total_cost_usd)}</p>
            <p className="text-xs text-gray-400 mt-1">Model {data.model}. Schatting op basis van Claude-tokentarieven.</p>
          </div>

          <div className="card p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left">
                  <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Tenant</th>
                  <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Plan</th>
                  <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Omgeving</th>
                  <th className="py-2 pr-4 font-medium text-gray-500 text-xs">Berichten</th>
                  <th className="py-2 font-medium text-gray-500 text-xs">Kosten</th>
                </tr>
              </thead>
              <tbody>
                {data.tenants.map(t => (
                  <tr key={t.tenant_id} className="border-b border-gray-50">
                    <td className="py-2 pr-4">
                      <Link to={`/admin/tenants/${t.tenant_id}`} className="text-brand-600 hover:underline font-medium">
                        {t.name}
                      </Link>
                      <span className="text-gray-400 text-xs ml-2">{t.slug}</span>
                    </td>
                    <td className="py-2 pr-4 text-gray-700 capitalize">{t.plan}</td>
                    <td className="py-2 pr-4">
                      {t.environment === 'test'
                        ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">test</span>
                        : <span className="text-gray-400 text-xs">prod</span>}
                    </td>
                    <td className="py-2 pr-4 text-gray-700">{t.message_count}</td>
                    <td className="py-2 text-gray-900 font-medium">{formatUsd(t.cost_usd)}</td>
                  </tr>
                ))}
                {data.tenants.length === 0 && (
                  <tr><td colSpan={5} className="py-4 text-gray-400">Geen tenants gevonden.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
